/**
 * CommsThread (conversation view)
 *
 * The unified per-person timeline, rendered as a messaging-style chat thread:
 * oldest at the top, newest at the bottom, day separators between calendar
 * days. SMS renders as bubbles (outbound primary/right, inbound gray/left) with a
 * sender + delivery-status meta line; calls render as compact event cards with
 * expandable speaker-labeled transcripts (Recruiter/Patient); notes as amber
 * sticky cards; system events as centered one-liners; email (Part 5) as a
 * bordered card with subject + delivery status.
 *
 * The data layer is unchanged from Chunk 2/3: GET /contacts/:id/communications
 * (newest-first, single-indexed on (contact_id, occurred_at DESC)), paginated
 * via the `before` cursor ("Load older" now sits at the TOP and scroll position
 * is preserved across prepends), live via the Supabase Realtime broadcast ping
 * on crm:contact:<id> plus a 60s fallback poll, and a `reloadToken` bump from
 * the composer. The component renders its own scroll region (flex-1 of the
 * parent conversation card), sticks to the bottom while the reader is near it,
 * and floats a "New messages" pill when they've scrolled up.
 *
 * `channelFilter='note'` narrows the whole thread to internal notes — the
 * recruiters' running record of a lead, consolidated (owner ask 2026-08-07,
 * entered by double-clicking the composer's Note pill). The narrowing is
 * server-side (?channel=note), so paging, the live merge and "Load older" all
 * stay inside notes instead of walking mixed windows that may hold none.
 *
 * `mask` (no-op default) is wired for Phase 7 role-aware masking: when true,
 * bodies on PHI-bearing channels (and call transcripts) are redacted while
 * structure/timestamps remain.
 */
import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Loader2,
  StickyNote,
  Activity,
  ArrowUpRight,
  ArrowDownLeft,
  MessageSquare,
  Mail,
  PhoneIncoming,
  PhoneOutgoing,
  FileText,
  ClipboardList,
  ChevronDown,
  ChevronRight,
  Paperclip,
  ImageOff,
  Voicemail,
  Play,
  X,
} from 'lucide-react';
import api from '../../services/api';
import { subscribeContactChanges } from '../../lib/realtimeTransport';
import { Communication, TeamMember } from '../types';
import { dayKey, dayLabel, messageTimestamp, prettySlug, formatPhone } from '../lib';
import { eligibilityBadge } from './shared';
import TranscriptModal from './TranscriptModal';
import FormResponseModal from './FormResponseModal';
import { Alert } from './ui';

const PAGE = 50;
const PHI_CHANNELS = new Set(['sms', 'call', 'email']);
const MISSED_CALL_STATUSES = new Set(['no-answer', 'busy', 'failed', 'canceled']);

interface Props {
  contactId: string;
  reloadToken?: number;
  mask?: boolean;
  /** id -> TeamMember, for sender attribution on outbound items (useTeam().byId). */
  usersById?: Map<string, TeamMember>;
  /** This contact's display name + phone — labels the PATIENT side of call
   *  transcripts (falls back to the number, then a generic 'Patient'). */
  contactName?: string;
  contactPhone?: string | null;
  /** Called on every Realtime broadcast ping — lets the parent refresh rail
   * cards (RecordsCard etc.) without bumping reloadToken, which would force
   * this thread's scroll-to-bottom. */
  onPing?: () => void;
  /** Escalation banner deep-link: scroll to + flash this message ("take you to
   *  where the concern was"). `focusNonce` re-triggers the jump on repeat clicks. */
  focusCommId?: string | null;
  focusNonce?: number;
  /** Notes-only view: when 'note', the thread shows (and pages through) ONLY
   *  internal notes — the recruiters' running record of this lead, consolidated
   *  in one place. Entered by double-clicking the composer's Note pill. The
   *  narrowing happens server-side (?channel=), so "Load older" walks notes
   *  instead of paging mixed windows that may hold none. */
  channelFilter?: 'note' | null;
  /** Leaves the notes-only view. Absent = the banner shows no way out (the
   *  host owns the toggle), so hosts that pass a filter should pass this too. */
  onExitFilter?: () => void;
  /** A flagged question in the AI screening summary card was clicked — open the
   *  agent card's screen detail on it (owner ask 2026-08-05: the flags the
   *  agent posts here have to reach the answer they came from). Absent = the
   *  summary card stays read-only. */
  onFocusScreen?: (questionKey: string) => void;
}

/** Union two lists by id and sort newest-first (keeps loaded history on poll). */
function mergeDesc(a: Communication[], b: Communication[]): Communication[] {
  const map = new Map<string, Communication>();
  for (const x of a) map.set(x.id, x);
  for (const x of b) map.set(x.id, x);
  return [...map.values()].sort(
    (x, y) => new Date(y.occurred_at).getTime() - new Date(x.occurred_at).getTime()
  );
}

function bodyFor(c: Communication, mask: boolean): string | null {
  if (!c.body) return null;
  if (mask && PHI_CHANNELS.has(c.channel)) return '•••••';
  return c.body;
}

/** mm:ss for a call duration in seconds; null when absent/zero. */
function fmtDuration(sec?: number): string | null {
  if (!sec || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** SMS delivery status -> short human word ('Failed' renders red). */
function smsStatus(status: string | null): { label: string; failed: boolean } {
  const s = (status || '').toLowerCase();
  if (s === 'delivered') return { label: 'Delivered', failed: false };
  if (s === 'failed' || s === 'undelivered') return { label: 'Failed', failed: true };
  if (['queued', 'sent', 'accepted', 'sending'].includes(s)) return { label: 'Sent', failed: false };
  if (!s) return { label: '', failed: false };
  return { label: s.charAt(0).toUpperCase() + s.slice(1), failed: false };
}

// ---------------------------------------------------------------------------
// Notes (owner asks 2026-08-05): long notes collapse instead of dominating the
// thread, and the AI screening summary renders as real question→answer rows —
// glanceable progress up top, what's flagged and what's still missing visible
// without reading a text blob.
// ---------------------------------------------------------------------------

interface SummaryItem {
  q: string;
  /** The question_key, when the note was written by a server that stamps it.
   *  Older notes carry only the question text — the jump falls back to that,
   *  which AgentPanel resolves by label. */
  k?: string;
  a: string | null;
  declined?: boolean;
  review?: boolean;
  source?: string;
  quote?: string;
}
interface SummaryData {
  answered: number;
  total: number;
  review: number;
  in_progress?: boolean;
  items: SummaryItem[];
  missing: string[];
}

const SUMMARY_SOURCE_LABEL: Record<string, string> = {
  questionnaire: 'from questionnaire',
  landing: 'from web form',
  prescreen: 'from earlier screen',
  earlier_chat: 'from earlier chat',
  derived: 'calculated',
};

/**
 * Fallback for summary notes written before metadata.summary existed: rebuild
 * the structure from the body text (a format we write ourselves, so the line
 * shapes are stable). Best-effort — returns null when nothing parses, and the
 * caller falls back to the plain note body.
 */
function parseSummaryBody(body: string | null): SummaryData | null {
  if (!body) return null;
  const lines = body.split('\n');
  const counts = lines.find((l) => l.startsWith('Collected '));
  const m = counts?.match(/^Collected (\d+)\/(\d+) screening answers · (\d+) clear · (\d+) for review/);
  if (!m) return null;
  const items: SummaryItem[] = [];
  for (const line of lines) {
    const im = line.match(/^• (.+?): (.*)$/);
    if (!im) continue;
    let a = im[2];
    const review = a.includes(' ⚠ review');
    let quote: string | undefined;
    const qm = a.match(/ — "(.*)"\s*$/);
    if (qm) { quote = qm[1]; a = a.slice(0, qm.index); }
    a = a.replace(' ⚠ review', '');
    let source: string | undefined;
    const sm = a.match(/ \((questionnaire|web form|earlier screen|earlier text chat|calculated)\)\s*$/);
    if (sm) {
      source = { 'web form': 'landing', 'earlier screen': 'prescreen', 'earlier text chat': 'earlier_chat', calculated: 'derived' }[sm[1]] || sm[1];
      a = a.slice(0, sm.index);
    }
    const declined = a.trim() === 'declined to answer';
    items.push({ q: im[1], a: declined ? null : a.trim(), ...(declined ? { declined: true } : {}), ...(review ? { review: true } : {}), ...(source ? { source } : {}), ...(quote ? { quote } : {}) });
  }
  const missingLine = lines.find((l) => l.startsWith('Not collected: '));
  const missing = missingLine
    ? missingLine.slice('Not collected: '.length).replace(/ \(\+\d+ more\)$/, '').split('; ').filter(Boolean)
    : [];
  return {
    answered: Number(m[1]),
    total: Number(m[2]),
    review: Number(m[4]),
    in_progress: /\(in progress\)/.test(lines[0] || ''),
    items,
    missing,
  };
}

/** Small count pill for the summary card's glance row. */
function SummaryPill({ tone, children }: { tone: 'gray' | 'amber' | 'green'; children: ReactNode }) {
  const cls = tone === 'amber'
    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
    : tone === 'green'
      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums ${cls}`}>
      {children}
    </span>
  );
}

/**
 * The AI screening summary as a structured, collapsible card. Collapsed by
 * default (it refreshes to the newest spot in the thread on every captured
 * answer — expanded, it shoved the conversation around every few minutes):
 * the glance row keeps progress + what's flagged readable at all times.
 *
 * Every flagged question is a link (owner ask 2026-08-05): clicking one opens
 * the agent card's screen detail on that answer, the same jump the escalation
 * banner makes — the flags the agent drops into the thread are the ones a
 * recruiter has to act on, so reading them here must reach them there.
 */
function AgentSummaryNote({ c, studyLine, onFocusScreen }: {
  c: Communication;
  studyLine: string | null;
  onFocusScreen?: (questionKey: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const meta: any = c.metadata || {};
  const data: SummaryData | null = meta.summary && Array.isArray(meta.summary.items)
    ? meta.summary
    : parseSummaryBody(c.body);
  const missingCount = data ? data.missing.length : 0;
  const flaggedItems = data ? data.items.filter((i) => i.review) : [];
  return (
    <div className="rounded-lg border-l-2 border-amber-400 bg-amber-50 p-3 dark:bg-amber-900/15">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 rounded text-left focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        <span className="inline-flex min-w-0 items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
          <StickyNote className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">
            AI screening summary{studyLine ? ` · ${studyLine}` : ''}
            {data?.in_progress ? ' · in progress' : ''}
          </span>
        </span>
        <span className="inline-flex shrink-0 items-center gap-1.5 text-[11px] text-gray-400">
          {messageTimestamp(c.occurred_at)}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {data ? (
        <>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <SummaryPill tone="gray">{data.answered}/{data.total} answered</SummaryPill>
            {data.review > 0 && <SummaryPill tone="amber">⚠ {data.review} to review</SummaryPill>}
            {missingCount > 0
              ? <SummaryPill tone="gray">{missingCount} not collected</SummaryPill>
              : data.total > 0 && data.answered >= data.total
                ? <SummaryPill tone="green">all collected</SummaryPill>
                : null}
          </div>
          {!open && flaggedItems.length > 0 && (
            /* Collapsed, the flagged questions ARE the card — each one is its
               own link to the answer behind it. No wrap: the row stays one
               line tall so the card never grows in the thread. */
            <p className="mt-1.5 flex min-w-0 items-baseline gap-1 overflow-hidden text-xs text-amber-700 dark:text-amber-400">
              <span aria-hidden="true">⚠</span>
              <span className="min-w-0 truncate">
                {flaggedItems.map((it, i) => (
                  <span key={i}>
                    {i > 0 && <span className="text-amber-500/70"> · </span>}
                    {onFocusScreen ? (
                      <button
                        type="button"
                        onClick={(ev) => { ev.stopPropagation(); onFocusScreen(it.k || it.q); }}
                        className="rounded underline decoration-amber-400 underline-offset-2 hover:text-amber-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:text-amber-200"
                        title="Open this answer in the agent's screening detail"
                      >
                        {it.q}
                      </button>
                    ) : it.q}
                  </span>
                ))}
              </span>
            </p>
          )}
          {open && (
            <div className="mt-2">
              <div className="divide-y divide-amber-200/70 dark:divide-amber-800/40">
                {data.items.map((it, i) => {
                  // A flagged row is the one a recruiter has to do something
                  // about, so it (and only it) becomes the link to the answer.
                  const linked = Boolean(it.review && onFocusScreen);
                  // Spans, not <p> — a linked row nests this inside a <button>,
                  // whose content model is phrasing only.
                  const body = (
                    <>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">{it.q}</span>
                      <span className={`mt-0.5 block text-sm font-medium ${it.review ? 'text-amber-800 dark:text-amber-300' : 'text-gray-900 dark:text-gray-100'}`}>
                        {it.declined ? <span className="font-normal italic text-gray-500 dark:text-gray-400">Declined to answer</span> : it.a}
                        {it.review && <span className="ml-1.5 font-normal text-amber-700 dark:text-amber-400">⚠ review</span>}
                        {it.source && SUMMARY_SOURCE_LABEL[it.source] && (
                          <span className="ml-1.5 text-xs font-normal text-gray-400 dark:text-gray-500">{SUMMARY_SOURCE_LABEL[it.source]}</span>
                        )}
                      </span>
                      {it.quote && (
                        <span className="mt-0.5 block text-xs italic text-gray-500 dark:text-gray-400">“{it.quote}”</span>
                      )}
                    </>
                  );
                  if (!linked) return <div key={i} className="py-1.5">{body}</div>;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => onFocusScreen!(it.k || it.q)}
                      title="Open this answer in the agent's screening detail"
                      className="flex w-full items-center gap-2 rounded py-1.5 text-left transition-colors duration-150 hover:bg-amber-100/60 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-amber-900/25"
                    >
                      <span className="min-w-0 flex-1">{body}</span>
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
              {missingCount > 0 && (
                <div className="mt-2 border-t border-amber-200/70 pt-2 dark:border-amber-800/40">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    Not collected ({missingCount})
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {data.missing.map((q, i) => (
                      <li key={i} className="text-xs text-gray-500 dark:text-gray-400">· {q}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        // Nothing parseable (a very old or truncated note) — plain body,
        // clamped unless opened.
        c.body && (
          <p className={`mt-1 whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-200 ${open ? '' : 'line-clamp-3'}`}>
            {c.body}
          </p>
        )
      )}
    </div>
  );
}

/** How long a plain note can be before it collapses behind "Show more". */
const NOTE_CLAMP_CHARS = 300;
const NOTE_CLAMP_LINES = 5;

/** Plain amber note card; long bodies collapse (owner ask 2026-08-05). */
function NoteCard({ c, author, body }: { c: Communication; author: string | null; body: string | null }) {
  const [open, setOpen] = useState(false);
  const long = Boolean(body && (body.length > NOTE_CLAMP_CHARS || body.split('\n').length > NOTE_CLAMP_LINES));
  return (
    <div className="rounded-lg border-l-2 border-amber-400 bg-amber-50 p-3 dark:bg-amber-900/15">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
          <StickyNote className="h-3.5 w-3.5" /> Note{author ? ` · ${author}` : ''}
        </span>
        <span className="text-[11px] text-gray-400">{messageTimestamp(c.occurred_at)}</span>
      </div>
      {body && (
        <p className={`mt-1 whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-200 ${long && !open ? 'line-clamp-4' : ''}`}>
          {body}
        </p>
      )}
      {long && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-1 text-xs font-medium text-amber-700 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-amber-400"
        >
          {open ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

/** Email delivery status -> short human word (bounced/complained/failed red). */
function emailStatus(status: string | null): { label: string; failed: boolean } {
  const s = (status || '').toLowerCase();
  if (s === 'delivered') return { label: 'Delivered', failed: false };
  if (s === 'bounced') return { label: 'Bounced', failed: true };
  if (s === 'complained') return { label: 'Marked as spam', failed: true };
  if (s === 'failed') return { label: 'Failed', failed: true };
  if (['queued', 'sent'].includes(s)) return { label: 'Sent', failed: false };
  if (!s) return { label: '', failed: false };
  return { label: s.charAt(0).toUpperCase() + s.slice(1), failed: false };
}

/** Call status -> human word ('no-answer' -> 'No answer'). */
function callStatusWord(status: string | null): string {
  const s = (status || '').toLowerCase().replace(/[-_]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

/**
 * Eligibility -> badge for the inline questionnaire card. Uses the shared
 * `eligibilityBadge` (single source of truth, identical to the lead-inbox study
 * chips and the historical "Screener answers" panel) but always returns a badge,
 * falling back to gray "Unknown" for an empty/unrecognized status.
 */
function leadEligibilityBadge(status: string | null): { label: string; cls: string } {
  return eligibilityBadge(status) || { label: 'Unknown', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' };
}

/** Highlight an individual answer that drove the eligibility outcome:
 *  'exclude' (disqualifying) → red, 'review' (flagged) → yellow, else none.
 *  Mirrors ScreenerPanel.answerHighlight so the inline card and the historical
 *  "Screener answers" panel highlight the SAME answers identically. */
function answerHighlight(kind: 'exclude' | 'review' | null | undefined): string {
  if (kind === 'exclude') return 'rounded-md bg-red-100 px-1.5 py-0.5 text-red-800 dark:bg-red-900/40 dark:text-red-300';
  if (kind === 'review') return 'rounded-md bg-yellow-100 px-1.5 py-0.5 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300';
  return 'text-gray-900 dark:text-gray-100';
}

/**
 * One MMS attachment (an entry in communications.metadata.media). Fetches a
 * short-lived presigned URL via the audited /documents/:id/url endpoint, then
 * renders an inline image (tap to open full size) or — for non-images — an
 * open-able chip. Respects `mask` (Phase 7): masked media is never fetched.
 */
interface MediaRef {
  document_id: string;
  content_type?: string | null;
  filename?: string | null;
}
function MmsAttachment({ media, mask }: { media: MediaRef; mask: boolean }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const isImage = String(media?.content_type || '').startsWith('image/');

  useEffect(() => {
    if (mask || !media?.document_id) return;
    let active = true;
    api
      .get(`/api/crm/documents/${media.document_id}/url`)
      .then((r) => { if (active) setUrl(r.data?.url || null); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [media?.document_id, mask]);

  const chip = (icon: ReactNode, label: string) => (
    <span className="inline-flex items-center gap-1 rounded-md bg-black/10 px-2 py-1 text-xs dark:bg-white/10">
      {icon} {label}
    </span>
  );

  if (mask) return chip(<Paperclip className="h-3 w-3" />, 'Attachment hidden');
  if (failed) return chip(<ImageOff className="h-3 w-3" />, 'Attachment unavailable');

  if (isImage) {
    if (!url) return <div className="h-40 w-40 animate-pulse rounded-lg bg-black/10 dark:bg-white/10" />;
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block">
        <img
          src={url}
          alt={media.filename || 'MMS attachment'}
          className="max-h-60 max-w-full rounded-lg object-cover"
          loading="lazy"
        />
      </a>
    );
  }
  // Non-image (pdf/vcard/etc.): open-able chip once the URL resolves.
  return (
    <a
      href={url || undefined}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex items-center gap-1.5 rounded-md bg-black/10 px-2 py-1 text-xs dark:bg-white/10 ${
        url ? '' : 'pointer-events-none opacity-60'
      }`}
    >
      <Paperclip className="h-3 w-3" /> {media.filename || 'Attachment'}
    </a>
  );
}

/**
 * Voicemail audio player. Click-to-load: the presigned URL is fetched ONLY when
 * the recruiter actually plays it, because issuing the URL writes a phi_accessed
 * audit row server-side — we must not audit-log a "play" for every voicemail just
 * because it scrolled into view. Respects `mask` (Phase 7).
 */
function VoicemailPlayer({ commId, mask }: { commId: string; mask: boolean }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = () => {
    if (url || loading) return;
    setLoading(true);
    api
      .get(`/api/crm/voice/voicemail/${commId}/audio`)
      .then((r) => setUrl(r.data?.url || null))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  };

  if (mask) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-black/10 px-2 py-1 text-xs dark:bg-white/10">
        <Voicemail className="h-3 w-3" /> Voicemail hidden
      </span>
    );
  }
  if (failed) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-black/10 px-2 py-1 text-xs text-red-600 dark:bg-white/10 dark:text-red-400">
        <Voicemail className="h-3 w-3" /> Voicemail unavailable
      </span>
    );
  }
  if (!url) {
    return (
      <button
        onClick={load}
        disabled={loading}
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary-300 px-2.5 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-primary-800 dark:text-primary-300 dark:hover:bg-primary-900/20 sm:min-h-[36px]"
      >
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
        Play voicemail
      </button>
    );
  }
  // eslint-disable-next-line jsx-a11y/media-has-caption
  return <audio src={url} controls preload="none" className="mt-0.5 h-9 w-full max-w-[16rem]" />;
}

export default function CommsThread({ contactId, reloadToken = 0, mask = false, usersById, onPing, contactName, contactPhone, focusCommId = null, focusNonce = 0, onFocusScreen, channelFilter = null, onExitFilter }: Props) {
  const [items, setItems] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The call whose full transcript is open in the modal (stores the full
  // payload so TranscriptModal — rendered outside renderItem — has everything
  // it needs). null when no transcript modal is open.
  const [transcriptModal, setTranscriptModal] = useState<{
    id: string;
    transcript: any[];
    direction: string | null;
    recruiter: string | null;
  } | null>(null);
  // The form_responses id whose answers are open in the questionnaire modal
  // (opened from a "Questionnaire submitted" timeline line). null when closed.
  const [responseModal, setResponseModal] = useState<string | null>(null);
  const [showNewPill, setShowNewPill] = useState(false);
  // Escalation banner deep-link: the message currently flashed after a jump-to.
  const [highlightId, setHighlightId] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null); // the inner rows wrapper (observed for async height growth)
  // Does the reader WANT the newest message pinned? This is an intent latch, not
  // a position test: it drops to false the moment they scroll backwards (reading
  // history) and only comes back when they return to the bottom themselves, hit
  // the "New messages" pill, or send. Every programmatic scroll below is gated on
  // it, so nothing — an arrival, a lazy image resolving, a re-render — can yank a
  // reader out of the history they deliberately scrolled into.
  const stickRef = useRef(true);
  const lastScrollTopRef = useRef(0); // last observed scrollTop, to tell up-scroll from down
  const didInitialScrollRef = useRef(false); // jumped to bottom on first load?
  const prependHeightRef = useRef<number | null>(null); // scrollHeight before a "Load older" prepend
  const lastNewestIdRef = useRef<string | null>(null); // detect genuinely-new arrivals
  const forceScrollRef = useRef(false); // the recruiter just sent — follow their own message down

  // Fetch the newest page. reset:true replaces (contact switch); else merge (poll/refresh).
  const fetchNewest = useCallback(async (reset: boolean) => {
    if (reset) setLoading(true);
    try {
      const res = await api.get(`/api/crm/contacts/${contactId}/communications`, {
        params: { limit: PAGE, ...(channelFilter ? { channel: channelFilter } : {}) },
      });
      const fetched: Communication[] = res.data?.communications || [];
      setItems((prev) => (reset ? fetched : mergeDesc(prev, fetched)));
      if (reset) setHasMore(!!res.data?.hasMore);
      setError(null);
    } catch (e: any) {
      if (reset) setError(e?.response?.data?.detail || e?.response?.data?.error || e.message);
    } finally {
      if (reset) setLoading(false);
    }
  }, [contactId, channelFilter]);

  // Reset + load when the contact changes (and reset scroll bookkeeping).
  useEffect(() => {
    didInitialScrollRef.current = false;
    lastNewestIdRef.current = null;
    prependHeightRef.current = null;
    stickRef.current = true;
    lastScrollTopRef.current = 0;
    forceScrollRef.current = false;
    setShowNewPill(false);
    fetchNewest(true);
  }, [fetchNewest]);

  // Live: subscribe to the contact's "changed" ping through the realtime transport
  // adapter (WP-3.3). The backend (service_role) emits a content-free ping at each
  // comms write; we re-fetch through the authed, RLS-gated API (crm.* stays
  // server-only — no browser PHI grants). Under the default 'supabase' transport
  // this is byte-identical to the previous per-contact broadcast subscription;
  // under 'sse' the same ping arrives over the shared EventSource.
  // Ref keeps the subscription stable across parent re-renders (onPing is a
  // fresh closure each render; resubscribing per render would thrash the stream).
  const onPingRef = useRef<(() => void) | undefined>(onPing);
  onPingRef.current = onPing;
  useEffect(() => {
    const unsubscribe = subscribeContactChanges(contactId, () => {
      fetchNewest(false);
      onPingRef.current?.();
    });
    return unsubscribe;
  }, [contactId, fetchNewest]);

  // Fallback poll (60s) in case a broadcast ping is ever missed (merges, so any
  // loaded history is preserved).
  useEffect(() => {
    const t = setInterval(() => fetchNewest(false), 60000);
    return () => clearInterval(t);
  }, [fetchNewest]);

  // Escalation banner deep-link: scroll the flagged message into view and flash
  // it. Best-effort — a message not in the loaded window (rare for a fresh
  // flag) simply doesn't scroll. focusNonce re-fires on repeat clicks.
  useEffect(() => {
    if (!focusCommId) return undefined;
    const el = document.getElementById(`comm-${focusCommId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      stickRef.current = false; // we deliberately left the bottom
      setShowNewPill(false);
      setHighlightId(focusCommId);
    }
    const clear = window.setTimeout(
      () => setHighlightId((id) => (id === focusCommId ? null : id)),
      2800,
    );
    return () => window.clearTimeout(clear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusCommId, focusNonce]);

  // Refresh when the parent signals a new message/note was sent — and follow
  // the recruiter's own message to the bottom even if they were scrolled up.
  // Not every reloadToken bump appends a message — an agent-draft decision, a
  // re-enroll, a rail action all bump it too. Disarm on a short timer so a bump
  // that added nothing can't leave the flag armed for minutes and then yank a
  // reader out of history the next time ANY message lands.
  useEffect(() => {
    if (!reloadToken) return undefined;
    forceScrollRef.current = true;
    fetchNewest(false);
    const t = window.setTimeout(() => { forceScrollRef.current = false; }, 4000);
    return () => window.clearTimeout(t);
  }, [reloadToken, fetchNewest]);

  const loadOlder = async () => {
    if (!items.length || loadingMore) return;
    stickRef.current = false; // asking for history is as deliberate as it gets
    setLoadingMore(true);
    try {
      const before = items[items.length - 1].occurred_at;
      const res = await api.get(`/api/crm/contacts/${contactId}/communications`, {
        params: { limit: PAGE, before, ...(channelFilter ? { channel: channelFilter } : {}) },
      });
      const older: Communication[] = res.data?.communications || [];
      // Capture the pre-prepend height HERE — after the await — so a live
      // broadcast/poll merge that committed while the request was in flight is
      // already part of the captured height and can't consume the adjustment
      // meant for this prepend.
      prependHeightRef.current = scrollRef.current?.scrollHeight ?? null;
      setItems((prev) => mergeDesc(prev, older));
      setHasMore(!!res.data?.hasMore);
    } catch {
      /* keep what we have */
      prependHeightRef.current = null;
    } finally {
      setLoadingMore(false);
    }
  };

  // Scroll behavior: first load jumps to the bottom; prepends preserve position;
  // new arrivals smooth-scroll only if the reader was already near the bottom,
  // otherwise the floating "New messages" pill appears.
  const newestId = items.length ? items[0].id : null;
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (prependHeightRef.current != null) {
      el.scrollTop += el.scrollHeight - prependHeightRef.current;
      lastScrollTopRef.current = el.scrollTop;
      prependHeightRef.current = null;
      lastNewestIdRef.current = newestId;
      return;
    }
    if (!didInitialScrollRef.current) {
      if (items.length) {
        el.scrollTop = el.scrollHeight;
        lastScrollTopRef.current = el.scrollTop;
        didInitialScrollRef.current = true;
        lastNewestIdRef.current = newestId;
      }
      return;
    }
    if (newestId && newestId !== lastNewestIdRef.current) {
      lastNewestIdRef.current = newestId;
      if (stickRef.current || forceScrollRef.current) {
        stickRef.current = true; // a recruiter's own send re-pins the thread
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        setShowNewPill(false);
      } else {
        setShowNewPill(true);
      }
      forceScrollRef.current = false;
    }
  }, [items, newestId]);

  // Keep the view pinned to the newest message when timeline content grows
  // AFTER the one-shot initial jump. MMS image attachments swap a 160px
  // placeholder for a real image, `<img loading="lazy">` decodes post-layout,
  // and web-font/line-wrap reflow all increase height asynchronously — without
  // this, the initial scrollHeight measurement (useLayoutEffect line ~371) is
  // stale and the thread opens parked ABOVE the newest message. The observer
  // re-pins only while the reader is still stuck to the bottom (stickRef — true
  // through the initial load, false the instant they scroll backwards), and never
  // mid-prepend, so it can't fight "Load older" or a reader reading history.
  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (loading || !el || !content || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (prependHeightRef.current != null) return;
      if (stickRef.current) {
        el.scrollTop = el.scrollHeight;
        lastScrollTopRef.current = el.scrollTop;
      }
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [loading, contactId]);

  // Any backward scroll is read as "I'm reading history" and unsticks the thread
  // for good; only landing back at the bottom (or the pill / a send) re-sticks it.
  // Our own programmatic scrolls all move DOWN and end at the bottom, so they
  // re-stick naturally instead of tripping the unstick.
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (el.scrollTop < lastScrollTopRef.current - 2) stickRef.current = false;
    if (atBottom) {
      stickRef.current = true;
      setShowNewPill(false);
    }
    lastScrollTopRef.current = el.scrollTop;
  };

  const jumpToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    stickRef.current = true;
    setShowNewPill(false);
  };

  const senderName = (userId: string | null): string | null => {
    if (userId === null || userId === undefined) return 'Automated';
    return usersById?.get(userId)?.name || null;
  };

  const renderItem = (c: Communication): ReactNode => {
    const body = bodyFor(c, mask);

    // ----- system: questionnaire submission -> rich answers card -----
    // A landing-page (or texted-form) submission posts its answers here so the
    // recruiter reads them inline in the conversation. Values mirror the
    // historical "Screener answers" panel; answer VALUES are PHI, so they
    // redact under `mask` while labels/structure stay.
    if (c.channel === 'system' && c.metadata?.event === 'lead_submitted') {
      const answers: { question: string; answer: string; eligibility_kind?: 'exclude' | 'review' | null }[] =
        Array.isArray(c.metadata?.answers) ? c.metadata.answers : [];
      const qName: string | null = c.metadata?.questionnaire_name || null;
      const study: string | null =
        c.metadata?.study_title || (c.metadata?.study_slug ? prettySlug(c.metadata.study_slug) : null);
      const badge = leadEligibilityBadge(c.metadata?.eligibility_status || null);
      // Partial-promoted leads post the SAME card as "in progress" and finalize it
      // on complete(); fall back to complete for legacy cards lacking the flag.
      const isComplete = c.metadata?.submission_complete !== false;
      return (
        <div
          key={c.id}
          className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 border-l-2 border-l-primary-400"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 dark:text-primary-300">
              <ClipboardList className="h-3.5 w-3.5" /> {isComplete ? 'Questionnaire submitted' : 'Questionnaire in progress'}
              {!isComplete && (
                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                  in progress
                </span>
              )}
            </span>
            <span className="shrink-0 text-[11px] text-gray-400">{messageTimestamp(c.occurred_at)}</span>
          </div>
          {(study || qName || badge) && (
            <div className="mt-1 flex items-start justify-between gap-2">
              <div className="min-w-0">
                {study && <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{study}</p>}
                {qName && qName !== study && <p className="truncate text-xs text-gray-400">{qName}</p>}
              </div>
              {badge && (
                <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${badge.cls}`}>
                  {badge.label}
                </span>
              )}
            </div>
          )}
          {answers.length > 0 ? (
            <div className="mt-2 divide-y divide-gray-100 dark:divide-gray-700">
              {answers.map((a, i) => (
                <div key={i} className="flex items-start justify-between gap-4 py-1">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{a.question}</span>
                  <span className={`break-words text-right text-sm font-medium ${answerHighlight(a.eligibility_kind)}`}>
                    {mask ? '•••' : a.answer}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-1 text-xs text-gray-400">{body || 'No answers recorded.'}</p>
          )}
        </div>
      );
    }

    // ----- system: texted/emailed questionnaire completed -> clickable line
    // that opens the answers in a modal. The completePublicResponse side-effect
    // stamps metadata.response_id; we route the actual answer fetch through the
    // PHI-audited /form-responses/:id endpoint (no answer VALUES ride in the
    // timeline row). Older events without a response_id fall through to the
    // plain one-liner below. -----
    if (c.channel === 'system' && c.metadata?.event === 'form_completed' && c.metadata?.response_id) {
      const rid = String(c.metadata.response_id);
      return (
        <div key={c.id} className="flex items-center justify-center py-0.5">
          <button
            type="button"
            onClick={() => setResponseModal(rid)}
            className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300 dark:hover:bg-primary-900/40"
          >
            <ClipboardList className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{c.body || 'Questionnaire submitted'}</span>
            <span className="shrink-0 underline underline-offset-2">View answers</span>
            <span className="shrink-0 text-primary-400 dark:text-primary-500/70">· {messageTimestamp(c.occurred_at)}</span>
          </button>
        </div>
      );
    }

    // ----- system: centered one-liner -----
    // Most system events (status/owner/study changes, callback booking, etc.)
    // stamp who did it in metadata.by_user_id, but the body text never names
    // them — surface it here, once, for every such event instead of teaching
    // each writer to embed a name in its body. A PRESENT key (even null, e.g.
    // a patient-booked callback) means the event captured provenance and is
    // worth a label; an ABSENT key (older/automated writers that never
    // recorded an actor) shows nothing rather than a misleading "Automated".
    if (c.channel === 'system') {
      const hasActor = c.metadata && 'by_user_id' in c.metadata;
      const actor = hasActor ? senderName(c.metadata.by_user_id ?? null) : null;
      return (
        <div key={c.id} className="flex items-center justify-center py-0.5">
          <span className="inline-flex items-center gap-1.5 text-center text-xs italic text-gray-400 dark:text-gray-500">
            <Activity className="h-3 w-3 shrink-0" />
            {c.body || 'System event'}{actor ? ` · ${actor}` : ''} · {messageTimestamp(c.occurred_at)}
          </span>
        </div>
      );
    }

    // ----- note: amber sticky card. The AI screening summary gets its own
    // structured, collapsible renderer (it refreshes to the newest spot in
    // the thread on every captured answer — see AgentSummaryNote); every
    // other note collapses when long (owner asks 2026-08-05). -----
    if (c.channel === 'note') {
      if (c.metadata?.event === 'agent_screening_summary') {
        const header = (c.body || '').split('\n')[0] || '';
        const parsedStudy = header.match(/^AI agent screening summary — (.+?)(?: \(in progress\))?$/)?.[1] || null;
        const studyLine = parsedStudy && parsedStudy !== 'study' ? parsedStudy : null;
        return <AgentSummaryNote key={c.id} c={c} studyLine={studyLine} onFocusScreen={onFocusScreen} />;
      }
      const author = c.user_id ? usersById?.get(c.user_id)?.name : null;
      return <NoteCard key={c.id} c={c} author={author || null} body={body} />;
    }

    const outbound = c.direction === 'outbound';

    // ----- sms/email skipped by the automation worker (opt-out/suppression at
    // send, merged contact, missing comprehensive form): body is null, the WHY
    // lives in metadata.reason — an empty bubble would hide it. Centered
    // one-liner.
    if ((c.channel === 'sms' || c.channel === 'email') && c.status === 'skipped' && !c.body) {
      const reason = String(c.metadata?.reason || 'not sent').replace(/_/g, ' ');
      return (
        <div key={c.id} className="flex items-center justify-center py-0.5">
          <span className="inline-flex items-center gap-1.5 text-center text-xs italic text-gray-400 dark:text-gray-500">
            <Activity className="h-3 w-3 shrink-0" />
            {c.channel === 'email' ? 'Email' : 'SMS'} skipped — {reason} · {messageTimestamp(c.occurred_at)}
          </span>
        </div>
      );
    }

    // ----- sms: chat bubbles -----
    if (c.channel === 'sms') {
      const st = smsStatus(c.status);
      const sender = outbound ? senderName(c.user_id) : null;
      const mediaList: MediaRef[] = Array.isArray(c.metadata?.media) ? c.metadata.media : [];
      const mediaErrors: any[] = Array.isArray(c.metadata?.media_errors) ? c.metadata.media_errors : [];
      const hasMedia = mediaList.length > 0 || mediaErrors.length > 0;
      return (
        <div key={c.id} className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
          <div className={`flex max-w-[75%] flex-col ${outbound ? 'items-end' : 'items-start'}`}>
            <div
              className={`whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-sm ${
                outbound
                  ? 'rounded-br-md bg-primary-600 text-white'
                  : 'rounded-bl-md bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
              }`}
            >
              {mediaList.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  {mediaList.map((m, i) => (
                    <MmsAttachment key={m.document_id || i} media={m} mask={mask} />
                  ))}
                </div>
              )}
              {mediaErrors.length > 0 && (
                <div className={`${mediaList.length ? 'mt-1.5 ' : ''}text-xs opacity-80`}>
                  ⚠ {mediaErrors.length} attachment{mediaErrors.length === 1 ? '' : 's'} couldn’t be saved
                </div>
              )}
              {body ? <div className={hasMedia ? 'mt-1.5' : ''}>{body}</div> : hasMedia ? null : ''}
            </div>
            <div className={`mt-0.5 px-1 text-[11px] text-gray-400 ${outbound ? 'text-right' : 'text-left'}`}>
              {messageTimestamp(c.occurred_at)}
              {/* Texting-agent sends carry metadata.agent (§5.2) — a subtle
                  "Team · Agent" attribution instead of a human sender name. */}
              {outbound && (c.metadata as any)?.agent ? (
                <span> · <span className="font-medium text-primary-600 dark:text-primary-400">Team · Agent</span></span>
              ) : sender ? <span> · {sender}</span> : null}
              {outbound && st.label ? (
                <span>
                  {' · '}
                  {st.failed ? <span className="font-medium text-red-500 dark:text-red-400">Failed</span> : st.label}
                </span>
              ) : null}
            </div>
            {c.metadata?.error_code && (
              <p className={`px-1 text-xs text-red-600 dark:text-red-400 ${outbound ? 'text-right' : ''}`}>
                ⚠ delivery error {c.metadata.error_code}{c.metadata.error_message ? `: ${c.metadata.error_message}` : ''}
              </p>
            )}
            {c.metadata?.overridden && (
              <p className={`px-1 text-xs text-amber-600 dark:text-amber-400 ${outbound ? 'text-right' : ''}`}>
                ↪ test override → {c.metadata.sent_to} (intended {c.metadata.intended_to || '—'})
              </p>
            )}
          </div>
        </div>
      );
    }

    // ----- email (Part 5): bordered card with subject + delivery status -----
    if (c.channel === 'email') {
      const st = emailStatus(c.status);
      const sender = outbound ? senderName(c.user_id) : null;
      const subject = c.metadata?.subject ? String(c.metadata.subject) : null;
      return (
        <div
          key={c.id}
          className={`rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 border-l-2 ${
            outbound ? 'border-l-primary-400' : 'border-l-gray-300 dark:border-l-gray-600'
          }`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              <Mail className={`h-3.5 w-3.5 ${outbound ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`} />
              Email
              {sender ? <span> · {sender}</span> : null}
              {st.label ? (
                <span>
                  {' · '}
                  {st.failed ? <span className="font-medium text-red-500 dark:text-red-400">{st.label}</span> : st.label}
                </span>
              ) : null}
            </span>
            <span className="shrink-0 text-[11px] text-gray-400">{messageTimestamp(c.occurred_at)}</span>
          </div>
          {subject && (
            <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{mask ? '•••••' : subject}</p>
          )}
          {body && <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-200">{body}</p>}
          {c.metadata?.error_message && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">⚠ {c.metadata.error_message}</p>
          )}
          {c.metadata?.overridden && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              ↪ test override → {c.metadata.sent_to} (intended {c.metadata.intended_to || '—'})
            </p>
          )}
        </div>
      );
    }

    // ----- call: full-width compact event card -----
    if (c.channel === 'call') {
      const s = (c.status || '').toLowerCase();
      const isVoicemail = !!c.metadata?.voicemail_s3_key || c.metadata?.voicemail === true;
      const missed = MISSED_CALL_STATUSES.has(s) && !isVoicemail;
      const Icon = isVoicemail ? Voicemail : (c.direction === 'inbound' ? PhoneIncoming : PhoneOutgoing);
      const duration = fmtDuration(c.metadata?.duration_sec || c.metadata?.voicemail_duration_sec);
      const statusWord = isVoicemail ? '' : callStatusWord(c.status);
      const recruiter = c.user_id ? usersById?.get(c.user_id)?.name : null;
      const transcript: any[] = Array.isArray(c.metadata?.transcript) ? c.metadata.transcript : [];
      const hasTranscript = transcript.length > 0;
      return (
        <div
          key={c.id}
          className="flex items-start gap-2.5 rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800"
        >
          <span
            className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
              missed
                ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                : 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {isVoicemail ? 'Voicemail' : c.direction === 'inbound' ? 'Incoming call' : 'Outgoing call'}
                {duration && <span className="font-normal text-gray-500 dark:text-gray-400"> · {duration}</span>}
                {statusWord && (
                  <span className={`font-normal ${missed ? 'text-red-500 dark:text-red-400' : 'text-gray-400'}`}>
                    {' · '}{statusWord}
                  </span>
                )}
              </span>
              <span className="shrink-0 text-[11px] text-gray-400">{messageTimestamp(c.occurred_at)}</span>
            </div>
            {recruiter && <p className="text-xs text-gray-400">{recruiter}</p>}
            {isVoicemail && <VoicemailPlayer commId={c.id} mask={mask} />}
            {body && !hasTranscript && (
              <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-700 dark:text-gray-300">{body}</p>
            )}
            {c.metadata?.overridden && (
              <p className="mt-0.5 text-xs text-amber-600 dark:text-amber-400">
                ↪ test override → {c.metadata.sent_to} (intended {c.metadata.intended_to || '—'})
              </p>
            )}
            {hasTranscript && (
              <div className="mt-1">
                {/* Brief collapsed preview: first ~2 segments, same speaker
                    logic as the modal, truncated so the card stays short. */}
                <div className="space-y-0.5 text-xs leading-snug text-gray-400 dark:text-gray-500">
                  {transcript.slice(0, 2).map((seg, i) => {
                    const rec = c.direction === 'inbound'
                      ? seg.track === 'outbound_track'
                      : seg.track === 'inbound_track';
                    const speaker = rec
                      ? (recruiter || 'Recruiter')
                      : (contactName || (contactPhone ? formatPhone(contactPhone) : null) || 'Patient');
                    const text = mask ? '•••••' : String(seg.text || '');
                    return (
                      <p key={i} className="line-clamp-1">
                        <span className="font-medium text-gray-500 dark:text-gray-400">{speaker}:</span>{' '}
                        {mask ? '•••••' : (text.length > 100 ? `${text.slice(0, 100)}…` : text)}
                      </p>
                    );
                  })}
                </div>
                <button
                  onClick={() => setTranscriptModal({
                    id: c.id,
                    transcript,
                    direction: c.direction ?? null,
                    recruiter: recruiter ?? null,
                  })}
                  className="mt-0.5 inline-flex min-h-[44px] items-center gap-1 rounded text-xs font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:text-primary-300 sm:min-h-[28px]"
                >
                  <FileText className="h-3.5 w-3.5" />
                  View full transcript ({transcript.length})
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    // ----- email (future) / anything else: bordered generic card -----
    const DirIcon = outbound ? ArrowUpRight : ArrowDownLeft;
    return (
      <div
        key={c.id}
        className={`rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 border-l-2 ${
          outbound ? 'border-l-primary-400' : 'border-l-gray-300 dark:border-l-gray-600'
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <DirIcon className={`h-3.5 w-3.5 ${outbound ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`} />
            {c.channel}{c.direction ? ` · ${c.direction}` : ''}
            {c.status ? <span className="text-gray-400"> · {c.status}</span> : null}
          </span>
          <span className="text-[11px] text-gray-400">{messageTimestamp(c.occurred_at)}</span>
        </div>
        {body && <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-200">{body}</p>}
      </div>
    );
  };

  // Notes-only banner. It carries the ONLY way back out (double-click is a poor
  // exit, and useless on touch), so it renders in EVERY branch below — loading,
  // error, empty and the thread itself.
  const filterBar =
    channelFilter === 'note' ? (
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-1.5 dark:border-amber-900/50 dark:bg-amber-900/20">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 dark:text-amber-300">
          <StickyNote className="h-3.5 w-3.5 shrink-0" />
          Notes only
          {!loading && !error && (
            <span className="font-normal text-amber-700/80 dark:text-amber-300/70">
              · {items.length}{hasMore ? '+' : ''} note{items.length === 1 && !hasMore ? '' : 's'}
            </span>
          )}
        </span>
        {onExitFilter && (
          <button
            type="button"
            onClick={onExitFilter}
            className="inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-amber-800 transition-colors duration-150 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-amber-300 dark:hover:bg-amber-900/40 sm:min-h-0"
          >
            <X className="h-3.5 w-3.5" /> Show everything
          </button>
        )}
      </div>
    ) : null;

  if (loading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {filterBar}
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {filterBar}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <Alert tone="error" icon={false}>{error}</Alert>
        </div>
      </div>
    );
  }

  // The C-12 "a draft is waiting — go review it in the console" strip used to
  // sit here. It is gone: AgentDraftDock now renders the actual draft, with
  // Approve / Edit / Reject, immediately below this thread. A strip pointing
  // at the console would be both redundant and wrong.

  if (!items.length) {
    const notesOnly = channelFilter === 'note';
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {filterBar}
        <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
          {notesOnly ? (
            <StickyNote className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
          ) : (
            <MessageSquare className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
          )}
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
            {notesOnly ? 'No notes on this lead yet' : 'No messages yet'}
          </p>
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            {notesOnly ? 'Add an internal note below and it lands here.' : 'Send a text or add a note below.'}
          </p>
        </div>
      </div>
    );
  }

  // Render ascending (oldest at top) with a separator at each calendar-day change.
  const asc = [...items].reverse();
  const rows: ReactNode[] = [];
  let lastDay: string | null = null;
  for (const c of asc) {
    const k = dayKey(c.occurred_at);
    if (k !== lastDay) {
      lastDay = k;
      rows.push(
        <div key={`day-${k}`} className="flex items-center gap-3 py-1.5">
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          <span className="text-xs text-gray-400">{dayLabel(c.occurred_at)}</span>
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        </div>
      );
    }
    rows.push(
      <div
        key={c.id}
        id={`comm-${c.id}`}
        className={`scroll-mt-2 rounded-xl transition-colors duration-500 ${
          highlightId === c.id
            ? 'bg-primary-50 ring-1 ring-primary-300 dark:bg-primary-900/20 dark:ring-primary-700'
            : ''
        }`}
      >
        {renderItem(c)}
      </div>,
    );
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {filterBar}
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-4 py-3">
        {hasMore && (
          <div className="flex justify-center pb-2">
            <button
              onClick={loadOlder}
              disabled={loadingMore}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
            >
              {loadingMore ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {loadingMore ? 'Loading…' : 'Load older'}
            </button>
          </div>
        )}
        <div ref={contentRef} className="space-y-2">{rows}</div>
      </div>
      {showNewPill && (
        <button
          onClick={jumpToBottom}
          className="absolute bottom-3 left-1/2 inline-flex min-h-[44px] -translate-x-1/2 items-center gap-1 rounded-full bg-primary-600 px-3 py-1.5 text-xs font-medium text-white shadow-md transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
        >
          <ChevronDown className="h-3.5 w-3.5" /> New messages
        </button>
      )}
      <TranscriptModal
        isOpen={!!transcriptModal}
        onClose={() => setTranscriptModal(null)}
        transcript={transcriptModal?.transcript || []}
        direction={transcriptModal?.direction ?? null}
        recruiter={transcriptModal?.recruiter ?? null}
        contactName={contactName}
        contactPhone={contactPhone}
        mask={mask}
      />
      <FormResponseModal
        responseId={responseModal}
        onClose={() => setResponseModal(null)}
        mask={mask}
      />
    </div>
  );
}
